import React, { Fragment, useEffect, useState } from 'react'
import SubHeader from '../components/sub-header/sub-header'
import SlidePreviewList from './slide-preview-list/slide-preview-list.container'
import SlideConstructor from './slide-constructor/slide-constructor.container'
import injectSheet from 'react-jss'
import SmallScreenPlaceholder from '../../../small-screen-placeholder/small-screen-placeholder'
import TabletPortraitPlaceholder from '../../../tablet-portrait-placeholder/tablet-portrait-placeholder'
import Skeleton from './skeleton/skeleton'
import Button from 'pobble-components/dist/button'
import SettingsModal from './modals/settings/settings'
import PendingArea from '../../../pending-area/pending-area'
import { useSubscriptionPolicy } from 'pobble-components/dist/subscriptions'
import PresentationControls from '../../../presentation-controls/presentation-controls'
import {
  fullVHExceptHeader,
  mediaTabletPortrait
} from 'pobble-components/dist/jss-mixins'
import ButtonElement from 'pobble-components/dist/button-element'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagic } from '@fortawesome/pro-solid-svg-icons/faMagic'
import { faCog } from '@fortawesome/pro-regular-svg-icons/faCog'
import {
  apiCopyPresentation,
  apiUpdatePresentationProperties
} from '../../../../common/api/lesson'
import { errorHandler } from 'pobble-components/dist/error-handler'
import { useHistory, useRouteMatch, useLocation } from 'react-router'
import Spinner from 'pobble-components/dist/spinner'
import { LP_SHOW_LAST_SLIDE } from 'pobble-components/dist/cookies'
import { useSelector } from 'react-redux'
import { useDispatch } from 'react-redux'
import { fetchPresentation } from '../../../../stores/presentation/presentation.actions'
import { openSettingsModal } from './redux/builder.actions'
import {
  getDuplicateSlideRequest,
  getFetchingRequest,
  getPresentation
} from '../../../../stores/presentation/presentation.selectors'
import NoItemsPlaceholder from 'pobble-components/dist/no-items-placeholder'
import { CreateSlideDialogs } from '../../../presentation-wizard/create-slide-dialogs'
import { EditSlideDialogs } from '../../../presentation-wizard/edit-slide-dialogs'
import { useSubscriptionDetails } from 'pobble-components/dist/subscriptions/utils'
import posthog from 'posthog-js'
import { apiClient } from 'pobble-components/dist/api'

const MissingPresentation = injectSheet({
  paragraph: {
    margin: '16px 0 !important'
  }
})(({ classes }) => {
  const history = useHistory()

  return (
    <NoItemsPlaceholder>
      <p>This presentation doesn't exist or you don't have access to it</p>
      <p className={classes.paragraph}>
        If you were given a link by your colleague<br />ask them to use "Create
        public link" option
      </p>
      <Button action={() => history.push('/')} tall>
        Go to saved Resources
      </Button>
    </NoItemsPlaceholder>
  )
})

const styles = ({ BREAKPOINTS, COLORS, FONT_SIZES }) => ({
  container: {
    ...fullVHExceptHeader,
    display: 'flex',
    flexDirection: 'column',
    background: 'var(--light-gray)',
    position: 'relative'
  },
  layoutContainer: {
    minHeight: '0',
    display: 'flex',
    flexDirection: 'row',
    marginTop: '20px',
    flex: '1',
    [`@media (max-width: 1023px)`]: {
      display: 'none'
    }
  },
  layoutSidebar: {
    width: '280px',
    height: '100%'
  },
  turnToPresentationWrapper: {
    padding: '0 20px 0 32px'
  },
  layoutContent: {
    flex: '1',
    height: 'calc(100% + 20px)',
    overflow: 'auto',
    marginTop: '-20px',
    padding: '20px',
    background: '#e3e3e3'
  },
  slideFooter: {
    marginTop: '48px'
  },
  configHolder: {
    flex: '1',
    display: 'flex',
    justifyContent: 'flex-end',
    alignSelf: 'center'
  },
  pendingArea: {
    animation: '0.6s fadeIn ease-in forwards',
    position: 'absolute',
    background: 'rgba(255, 255, 255, 0.5)'
  },
  mobilePlaceholder: {
    flex: '1',
    borderTop: '1px solid #eee',
    display: 'none',
    [`@media (max-width: 1023px)`]: {
      display: 'flex'
    },
    ...mediaTabletPortrait({
      display: 'none'
    })
  },
  tabletPortraitPlaceholder: {
    flex: '1',
    display: 'none',
    borderTop: '1px solid #eee',
    ...mediaTabletPortrait({
      display: 'flex'
    })
  },
  assetToPresentation: {
    width: '100%',
    height: '134px',
    borderRadius: '4px',
    background: '#fff',
    color: COLORS.BLUE.NORMAL,
    ...FONT_SIZES.BODY_LARGE.STYLE,
    fontWeight: 'bold',
    border: `1px dashed ${COLORS.BLUE.NORMAL}`,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    '&:hover': {
      borderStyle: 'solid'
    }
  },
  assetToPresentationIcon: {
    marginBottom: '8px',
    width: '52px',
    height: '52px',
    borderRadius: '100%',
    backgroundColor: 'rgb(0 175 236 / 10%)',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex'
  },
  assetHint: {
    ...FONT_SIZES.BODY_SMALL.STYLE,
    color: COLORS.TEXT.MID_GREY.NORMAL,
    textAlign: 'center'
  },
  spinner: {
    fontSize: '24px'
  },
  settings: {
    marginLeft: '8px'
  },
  '@keyframes fadeIn': {
    from: { opacity: '0' },
    to: { opacity: '1' }
  }
})

const Builder = ({ classes }) => {
  const dispatch = useDispatch()
  const match = useRouteMatch()
  const location = useLocation()
  const history = useHistory()
  const [pending, setPending] = useState(true)
  const { hasAccessToTL, freeTrial } = useSubscriptionDetails()

  const duplicateSlideRequest = useSelector(getDuplicateSlideRequest)

  const presentation = useSelector(getPresentation)
  const presentationRequest = useSelector(getFetchingRequest)

  const missingPresentation =
    presentationRequest.requestError &&
    presentationRequest.requestError.response &&
    (presentationRequest.requestError.response.status === 404 ||
      presentationRequest.requestError.response.status === 403)

  const [turnToPresentationPending, setTurnToPresentationPending] = useState(
    false
  )

  const fromPrompt = location.state?.fromPrompt

  // Fetch presentation
  useEffect(
    () => {
      dispatch(fetchPresentation(match.params.id))
        .then(() => setPending(false))
        .catch(() => setPending(false))
    },
    [match.params.id, dispatch]
  )

  /**
   * Show users that they work with a copy
   *   when they clicked "Use now"
   */
  useEffect(
    () => {
      if (fromPrompt) {
        //toast.info('This editable copy is saved to your resources')
      }
    },
    [fromPrompt]
  )

  const editSp = useSubscriptionPolicy(
    sp => sp.lesson_planning.edit_presentation
  )

  const turnIntoPresentation = () => {
    setTurnToPresentationPending(true)
    apiCopyPresentation(presentation.id, {
      skip_copy_in_title: true
    })
      .then(res => {
        const id = res.data.presentation.id
        apiUpdatePresentationProperties(id, {
          asset: false
        })
          .then(() => {
            LP_SHOW_LAST_SLIDE.set(true)
            history.push(`/lesson/${id}`)
          })
          .catch(errorHandler)
      })
      .catch(errorHandler)
  }

  const presentationFetched = !pending && !!presentation && !missingPresentation

  useEffect(
    () => {
      if (presentationFetched) {
        apiClient.post('/v3/me/activated_with_product', {
          type: 'feature'
        })
        posthog.capture('activate', { type: 'feature' })
        posthog.capture('builder view', {
          hasAccessToTL,
          freeTrial,
          title: presentation.title
        })
      }
    },
    [presentationFetched, presentation, hasAccessToTL, freeTrial]
  )

  return (
    <div className={`${classes.container}`}>
      <SubHeader>
        {presentationFetched && (
          <>
            <div className={classes.configHolder}>
              <PresentationControls presentation={presentation} />
              <Button
                className={classes.settings}
                tall
                icon={<FontAwesomeIcon icon={faCog} />}
                simple
                action={
                  editSp.restricted
                    ? editSp.showDialog
                    : () => dispatch(openSettingsModal())
                }>
                Settings
              </Button>
            </div>
          </>
        )}
      </SubHeader>
      {presentationFetched && (
        <Fragment>
          <div className={classes.layoutContainer}>
            {!presentation.asset && (
              <div className={classes.layoutSidebar}>
                <SlidePreviewList />
              </div>
            )}
            {!!presentation.asset && (
              <div className={classes.layoutSidebar}>
                <div className={classes.turnToPresentationWrapper}>
                  <ButtonElement
                    onClick={() => {
                      if (!turnToPresentationPending) {
                        turnIntoPresentation()
                      }
                    }}
                    className={classes.assetToPresentation}>
                    <span className={classes.assetToPresentationIcon}>
                      {turnToPresentationPending && (
                        <Spinner className={classes.spinner} />
                      )}
                      {!turnToPresentationPending && (
                        <FontAwesomeIcon icon={faMagic} />
                      )}
                    </span>
                    <span>Turn into a presentation</span>
                  </ButtonElement>
                  <p className={classes.assetHint}>
                    Create a full lesson presentation from this resource. Add
                    extra resources and slide types in a few clicks!
                  </p>
                </div>
              </div>
            )}
            <div
              className={`${
                classes.layoutContent
              } metrics__builder_slide_wrapper`}>
              <SlideConstructor />
            </div>
            <CreateSlideDialogs />
            <EditSlideDialogs />
          </div>
          <SmallScreenPlaceholder className={classes.mobilePlaceholder} />
          <TabletPortraitPlaceholder
            className={classes.tabletPortraitPlaceholder}
          />
          {duplicateSlideRequest.pending && (
            <PendingArea className={classes.pendingArea} />
          )}
        </Fragment>
      )}
      <SettingsModal />
      {!!pending && !missingPresentation && <Skeleton />}
      {missingPresentation && <MissingPresentation />}
    </div>
  )
}

const BuilderWrapper = ({ ...props }) => {
  const match = useRouteMatch()

  return <Builder key={match.params.id} {...props} />
}

export default injectSheet(styles)(BuilderWrapper)
